import React, { useState, useEffect, useMemo } from 'react';
import { fmtNum, fmtFull } from '../utils/formatters';
import { ClientRecord, Campaign, Demographics } from '../data';
import { getClients, getAllCampaigns, getReachCampaigns, getCampaign, getCampaignHourlySeries, getCampaignHourlyAverageSeries, getCampaignCreatives, CampaignCreative, getCampaignDisplayUnitIds, getScreensByBroadsignIds, getCampaignImpressionsByDisplayUnit, getCampaignImpressionsByCreative, CreativeImpressionsResult, EntityTotals, HourlyPoint, HourlyAveragePoint, getCampaignDemographics, getScreensSummary } from '../adapters/campaigns';
import { Screen } from '../data';
import { SeriesPoint } from '../data';
import { KPICard } from './KPI';
import { TimeSeriesChart } from './Chart';
import { NetworkMap } from './NetworkMap';
import { DemographicsCard } from './Demographics';
import { DateRangePicker } from './DateRangePicker';
import { CampaignsPage } from './Campaigns';
import { CreativesPage } from './Creatives';
import { ScreensPage } from './Screens';
import { SettingsPage } from './Settings';
import { PopReport } from './PopReport';

const SERIES = [
  { key: 'plays',       label: 'Plays',       color: 'var(--series-plays)', isMoney: false },
  { key: 'impressions', label: 'Impressions', color: 'var(--series-impr)',  isMoney: false },
];

function parseRoute(path: string): { page: string; campaignUuid: string | null } {
  const m = path.match(/^\/campaigns\/([^/]+)\/?$/);
  if (m) return { page: 'campaign', campaignUuid: m[1] };
  const map: Record<string, string> = {
    '/campaigns': 'campaigns', '/creatives': 'creatives',
    '/screens': 'screens', '/settings': 'settings',
  };
  return { page: map[path] ?? 'campaigns', campaignUuid: null };
}

const NAV = [
  { key: 'campaigns', label: 'Campaigns', icon: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7' },
  { key: 'creatives', label: 'Creatives', icon: 'M4 4h16v16H4zM4 9h16M9 4v16' },
  { key: 'screens',   label: 'Screens',   icon: 'M3 4h18v12H3zM8 20h8M12 16v4' },
];
const SETTINGS_ICON = 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z';

function Icon({ d, size = 16, fill = 'none' }: { d: string; size?: number; fill?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

function iconBtn(badge?: boolean): React.CSSProperties {
  return {
    width: 34, height: 34, borderRadius: 8,
    background: 'transparent', border: '1px solid var(--border-soft)',
    color: 'var(--fg-default)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', cursor: 'pointer',
  };
}

function pillBtn(): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    background: 'var(--bg-surface)', border: '1px solid var(--border-soft)',
    color: 'var(--fg-default)', padding: '7px 12px', borderRadius: 8,
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
  };
}

function primaryBtn(): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'var(--brand-navy-800)', color: '#fff', border: 'none',
    padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };
}

function initials(user?: { first_name?: string; last_name?: string; username?: string; email?: string }): string {
  if (user?.first_name && user?.last_name) return (user.first_name[0] + user.last_name[0]).toUpperCase();
  if (user?.first_name) return user.first_name.slice(0, 2).toUpperCase();
  const src = user?.username || user?.email || '';
  const parts = src.split(/[._\-@]/);
  if (parts.length >= 2 && parts[0] && parts[1]) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase() || '?';
}

function displayName(user?: { first_name?: string; last_name?: string; username?: string; email?: string }): string {
  if (user?.first_name || user?.last_name) return [user.first_name, user.last_name].filter(Boolean).join(' ');
  return user?.username || user?.email || 'My account';
}

function clientInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function ExportModal({ campaign, startDate, endDate, onClose, onOpenReport }: {
  campaign: any; startDate: string; endDate: string;
  onClose: () => void;
  onOpenReport: () => void;
}) {
  const [selected, setSelected] = useState<'report' | 'csv'>('report');

  const OPTIONS = [
    { key: 'report' as const, label: 'Executive Summary', sub: 'Branded one-page PDF, ready to share with stakeholders', disabled: false },
    { key: 'csv' as const,    label: 'CSV Export',        sub: 'Raw delivery data by hour, creative, and screen',        disabled: true },
  ];

  const handleContinue = () => {
    if (selected === 'report') onOpenReport();
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div className="card" style={{ padding: 24, minWidth: 360, maxWidth: 440, width: '90%' }} onClick={e => e.stopPropagation()}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 4 }}>Export</div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            {campaign?.name} · {startDate}{endDate ? ` – ${endDate}` : ''}
          </div>
        </div>
        {OPTIONS.map(({ key, label, sub, disabled }) => (
          <label key={key} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 0',
            borderBottom: '1px solid var(--border-subtle)',
            cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
          }}>
            <input type="radio" name="export-option" checked={selected === key} disabled={disabled} onChange={() => setSelected(key)}
              style={{ marginTop: 2, width: 15, height: 15, accentColor: 'var(--brand-green-500)', cursor: disabled ? 'default' : 'pointer', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-strong)' }}>{label}</span>
                {disabled && <span style={{ fontSize: 10.5, color: 'var(--fg-muted)', background: 'var(--bg-surface-2)', padding: '1px 7px', borderRadius: 999 }}>Coming soon</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>{sub}</div>
            </div>
          </label>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 12, color: 'var(--fg-default)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleContinue} style={primaryBtn()}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ page, setPage, collapsed, setCollapsed, theme, toggleTheme, onLogout, user, clients, clientsLoading, selectedClientUuid, onClientChange }: {
  page: string; setPage: (p: string) => void;
  collapsed: boolean; setCollapsed: (fn: (c: boolean) => boolean) => void;
  theme: string; toggleTheme: () => void;
  onLogout?: () => void;
  user?: { first_name?: string; last_name?: string; username?: string; email?: string };
  clients: ClientRecord[];
  clientsLoading: boolean;
  selectedClientUuid: string;
  onClientChange: (uuid: string) => void;
}) {
  return (
    <aside style={{
      position: 'sticky', top: 0, alignSelf: 'flex-start',
      height: '100vh', width: collapsed ? 64 : 224,
      background: 'var(--bg-sidebar)', color: '#fff',
      display: 'flex', flexDirection: 'column',
      boxShadow: 'var(--shadow-sidebar)',
      transition: 'width 0.2s ease', zIndex: 5, flexShrink: 0,
    }}>
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', height: 60, display: 'flex', alignItems: 'center', padding: collapsed ? '0' : '0 18px', justifyContent: collapsed ? 'center' : 'flex-start', gap: 10 }}>
        <button onClick={() => setPage('campaigns')} title="Home" style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', flex: collapsed ? undefined : 1, minWidth: 0 }}>
          {collapsed
            ? <img src="/favicon.svg" alt="Smartstreet" style={{ width: 28, height: 28 }} />
            : <img src="/logo-dark.svg" alt="Smartstreet" style={{ height: 24, width: 'auto', maxWidth: '100%', objectFit: 'contain', objectPosition: 'left' }} />
          }
        </button>
        <button onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.14)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0 }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 640 640" fill="currentColor">
            <path d="M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z" />
          </svg>
        </button>
      </div>

      {!collapsed && (
        <div style={{ margin: '14px 12px 8px', padding: '10px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', marginBottom: 6 }}>Client</div>
          {clientsLoading ? (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>Loading…</div>
          ) : clients.length === 0 ? (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>No clients</div>
          ) : (
            <select
              value={selectedClientUuid}
              onChange={e => onClientChange(e.target.value)}
              style={{
                width: '100%', padding: '6px 8px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 7, color: '#fff',
                fontSize: 12, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
              }}
            >
              {clients.map(c => (
                <option key={c.uuid} value={c.uuid} style={{ background: '#0c1b33', color: '#fff' }}>{c.name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      <nav style={{ flex: 1, padding: '8px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!collapsed && <div style={{ padding: '8px 12px 4px', fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)' }}>Navigation</div>}
        {NAV.map(n => {
          const active = page === n.key;
          return (
            <button key={n.key} onClick={() => setPage(n.key)} title={n.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: collapsed ? '10px 0' : '9px 12px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 8, border: 'none', textAlign: 'left',
                background: active ? 'var(--brand-green-500)' : 'transparent',
                color: active ? 'var(--brand-navy-900)' : 'rgba(255,255,255,0.92)',
                fontWeight: active ? 700 : 500, fontSize: 13,
                transition: 'background 0.15s, color 0.15s', cursor: 'pointer', width: '100%',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.92)'; } }}
            >
              <Icon d={n.icon} size={17} />
              {!collapsed && <span>{n.label}</span>}
              {!collapsed && active && <span style={{ marginLeft: 'auto', fontSize: 10 }}>●</span>}
            </button>
          );
        })}
      </nav>

      <div style={{ padding: 8, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '8px 0' : '8px 12px', justifyContent: collapsed ? 'center' : 'flex-start', border: 'none', background: 'transparent', borderRadius: 8, color: 'rgba(255,255,255,0.88)', fontSize: 12, cursor: 'pointer', width: '100%' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {theme === 'dark'
            ? <Icon d="M12 3v1M12 20v1M4.2 4.2l.7.7M19.1 19.1l.7.7M3 12h1M20 12h1M4.2 19.8l.7-.7M19.1 4.9l.7-.7M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" size={16} />
            : <Icon d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" size={16} />}
          {!collapsed && <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
        </button>
        <button onClick={() => setPage('settings')} title="Settings"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '8px 0' : '8px 12px', justifyContent: collapsed ? 'center' : 'flex-start', border: 'none', background: page === 'settings' ? 'var(--brand-green-500)' : 'transparent', borderRadius: 8, color: page === 'settings' ? 'var(--brand-navy-900)' : 'rgba(255,255,255,0.7)', fontWeight: page === 'settings' ? 700 : 500, fontSize: 12, cursor: 'pointer', width: '100%' }}
          onMouseEnter={e => { if (page !== 'settings') { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; } }}
          onMouseLeave={e => { if (page !== 'settings') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; } }}
        >
          <Icon d={SETTINGS_ICON} size={16} />
          {!collapsed && <span>Settings</span>}
        </button>
        {!collapsed && (
          <div style={{ margin: '4px 4px', padding: '12px 14px', background: 'rgba(0,200,150,0.07)', border: '1px solid rgba(0,200,150,0.18)', borderRadius: 10 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--brand-green-500)', marginBottom: 5 }}>Smartstreet Reports</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.82)', lineHeight: 1.45 }}>Verified by on-screen sensors. No personal data collected.</div>
          </div>
        )}
        {onLogout && (
          <button onClick={onLogout} title="Log out"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '8px 0' : '8px 12px', justifyContent: collapsed ? 'center' : 'flex-start', border: 'none', background: 'transparent', borderRadius: 8, color: 'rgba(255,255,255,0.78)', fontSize: 12, cursor: 'pointer', width: '100%' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
          >
            <Icon d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" size={16} />
            {!collapsed && <span>Log out</span>}
          </button>
        )}
      </div>
    </aside>
  );
}

function TopBar({ theme, toggleTheme, title, subtitle, eyebrow, backTo, onBack, user }: {
  theme: string; toggleTheme: () => void;
  title: string; subtitle?: React.ReactNode; eyebrow?: string;
  backTo?: string | null; onBack?: () => void;
  user?: { first_name?: string; last_name?: string; username?: string; email?: string };
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)', position: 'sticky', top: 0, zIndex: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {backTo && (
          <button onClick={onBack} style={{ ...iconBtn(), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d="M19 12H5M11 5l-7 7 7 7" size={16} />
          </button>
        )}
        <div>
          {eyebrow && <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 2 }}>{eyebrow}</div>}
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg-strong)', letterSpacing: '-0.01em' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} style={iconBtn()}>
          {theme === 'dark'
            ? <Icon d="M12 3v1M12 20v1M4.2 4.2l.7.7M19.1 19.1l.7.7M3 12h1M20 12h1M4.2 19.8l.7-.7M19.1 4.9l.7-.7M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" size={16} />
            : <Icon d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" size={16} />}
        </button>
        <div style={{ width: 1, height: 24, background: 'var(--border-soft)', margin: '0 4px' }} />
        <div title={displayName(user)} style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#00c896,#0097a7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: 'var(--brand-navy-900)' }}>
          {initials(user)}
        </div>
      </div>
    </div>
  );
}

function ControlsBar({ granularity, setGranularity, startDate, endDate, onDateChange, onExport }: {
  granularity: string; setGranularity: (g: string) => void;
  startDate: string; endDate: string; onDateChange: (start: string, end: string) => void;
  onExport?: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <DateRangePicker startDate={startDate} endDate={endDate} onDateChange={onDateChange} />
        <button style={pillBtn()}>
          <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" size={14} />
          <span style={{ color: 'var(--fg-muted)' }}>America/New_York</span>
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'inline-flex', background: 'var(--bg-surface-2)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: 3 }}>
          {['Hourly', 'Daily', 'Monthly'].map(g => (
            <button key={g} onClick={() => setGranularity(g)} style={{ border: 'none', background: granularity === g ? 'var(--bg-surface)' : 'transparent', color: granularity === g ? 'var(--fg-strong)' : 'var(--fg-muted)', fontWeight: granularity === g ? 700 : 500, padding: '5px 14px', borderRadius: 6, fontSize: 12, boxShadow: granularity === g ? '0 1px 2px rgba(15,23,42,0.08)' : 'none', cursor: 'pointer' }}>{g}</button>
          ))}
        </div>
        <button style={primaryBtn()} onClick={onExport}>
          <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" size={14} />
          Export
        </button>
      </div>
    </div>
  );
}


function MiniMetric({ label, value, delta, sub }: { label: string; value: string; delta?: number; sub?: string }) {
  const positive = (delta ?? 0) >= 0;
  return (
    <div className="card" style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div className="eyebrow" style={{ marginBottom: 4 }}>{label}</div>
        <div className="num" style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg-strong)' }}>{value}</div>
        {sub && <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
      {delta != null && <span style={{ fontSize: 11, fontWeight: 700, color: positive ? 'var(--brand-green-500)' : '#dc2626', background: positive ? 'var(--accent-soft-bg)' : 'rgba(220,38,38,0.10)', border: '1px solid ' + (positive ? 'var(--accent-soft-border)' : 'rgba(220,38,38,0.2)'), padding: '3px 8px', borderRadius: 999 }}>
        <span>{positive ? '▲' : '▼'}</span>
        <span className="num"> {Math.abs(delta!).toFixed(1)}%</span>
      </span>}
    </div>
  );
}

function CreativesTable({ creatives }: { creatives: any[] }) {
  const [sortKey, setSortKey] = useState('plays');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const sorted = useMemo(() => {
    return [...creatives].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [creatives, sortKey, sortDir]);

  const cols = [
    { key: 'thumb',      label: '',               sortable: false, width: 54,  stickyLeft: 0 },
    { key: 'name',       label: 'Creative',        sortable: true,             stickyLeft: 54 },
    { key: 'type',       label: 'Type',            sortable: true,  width: 70 },
    { key: 'plays',      label: 'Plays',           sortable: true,  width: 80,  num: true },
    { key: 'impressions',label: 'Impressions',     sortable: true,  width: 110, num: true },
    { key: 'views',      label: 'Verified Views',  sortable: true,  width: 120, num: true, sensor: true },
    { key: 'completion', label: 'Completion',      sortable: true,  width: 110, num: true, sensor: true },
  ];

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const showColors: Record<string, string> = {
    'Stranger Things': 'linear-gradient(135deg,#dc2626 0%,#7f1d1d 100%)',
    'Harry Potter':    'linear-gradient(135deg,#7c3aed 0%,#1e1b4b 100%)',
  };

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>Creative breakdown</div>
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', background: 'var(--bg-surface-2)', padding: '2px 8px', borderRadius: 999 }}>{creatives.length} creatives</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--border-soft)', padding: '6px 12px', borderRadius: 8, fontSize: 11.5, fontWeight: 600, color: 'var(--fg-default)', cursor: 'pointer' }}>
            <Icon d="M3 6h18M3 12h18M3 18h18" size={13} /> Columns
          </button>
          <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--bg-surface-2)', border: '1px solid var(--border-soft)', padding: '6px 12px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, color: 'var(--fg-default)', cursor: 'pointer' }}>
            <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" size={12} /> CSV
          </button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
          <thead>
            <tr style={{ background: 'var(--bg-surface-2)' }}>
              {cols.map(c => (
                <th key={c.key} onClick={() => c.sortable && handleSort(c.key)}
                  style={{ textAlign: c.num ? 'right' : 'left', padding: '10px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', cursor: c.sortable ? 'pointer' : 'default', width: c.width, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-subtle)', ...(c.stickyLeft !== undefined ? { position: 'sticky', left: c.stickyLeft, zIndex: 2, background: 'var(--bg-surface-2)' } : {}) }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: sortKey === c.key ? 'var(--fg-strong)' : 'inherit' }}>
                    {c.sensor && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--brand-green-500)' }} />}
                    {c.label}
                    {c.sortable && sortKey === c.key && <span style={{ fontSize: 9 }}>{sortDir === 'desc' ? '▼' : '▲'}</span>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => {
              const bg = showColors[row.show] || 'linear-gradient(135deg,#475569,#1e293b)';
              const initial = row.show === 'Stranger Things' ? 'ST' : row.show === 'Harry Potter' ? 'HP' : (row.show?.slice(0, 2) || '');
              return (
                <tr key={row.id} className="tbody-row" style={{ borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.1s' }}>
                  <td style={{ padding: '10px 14px', position: 'sticky', left: 0, zIndex: 1, background: 'var(--bg-surface)' }}>
                    <div style={{ width: 32, height: 40, borderRadius: 5, background: bg, position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', boxShadow: 'inset 0 -12px 14px rgba(0,0,0,0.25)' }}>
                      <span style={{ position: 'absolute', top: 4, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.06em' }}>{initial}</span>
                      {row.type === 'Video' && <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffffe0', fontSize: 11 }}>▶</span>}
                      <span style={{ fontSize: 8, fontWeight: 700, background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '1px 3px', borderRadius: 2, margin: 2 }} className="num">{row.dur}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', position: 'sticky', left: 54, zIndex: 1, background: 'var(--bg-surface)' }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, color: 'var(--fg-strong)', marginBottom: 2 }}>{row.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: 'var(--fg-muted)' }}>
                      <span className="num">{row.id}</span>
                      <span style={{ opacity: 0.5 }}>·</span>
                      <span style={{ background: row.show === 'Stranger Things' ? 'rgba(220,38,38,0.10)' : 'rgba(124,58,237,0.10)', color: row.show === 'Stranger Things' ? '#dc2626' : '#7c3aed', padding: '1px 7px', borderRadius: 999, fontWeight: 700, fontSize: 10 }}>{row.show}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ display: 'inline-block', fontSize: 10.5, fontWeight: 700, background: row.type === 'Video' ? 'rgba(96,165,250,0.14)' : 'rgba(255,92,0,0.10)', color: row.type === 'Video' ? 'var(--brand-blue)' : '#FF5C00', border: `1px solid ${row.type === 'Video' ? 'rgba(96,165,250,0.25)' : 'rgba(255,92,0,0.30)'}`, padding: '2px 8px', borderRadius: 999 }}>{row.type}</span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }} className="num">{fmtFull(row.plays)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: 'var(--fg-strong)' }} className="num">{fmtFull(row.impressions)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }} className="num">
                    <div style={{ fontWeight: 700, color: 'var(--brand-green-500)' }}>{fmtFull(row.views)}</div>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    {row.completion != null ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 36, height: 4, background: 'var(--bg-surface-3)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: `${row.completion}%`, height: '100%', background: 'var(--brand-green-500)' }} />
                        </div>
                        <span className="num" style={{ fontWeight: 700, color: 'var(--fg-strong)', minWidth: 38, textAlign: 'right' }}>{row.completion}%</span>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--fg-faint)', fontSize: 11 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '10px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, color: 'var(--fg-muted)', background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-subtle)' }}>
        <span>Showing {sorted.length} of {creatives.length}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {['‹', '1', '›'].map((label, i) => (
            <button key={i} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-soft)', background: 'var(--bg-surface)', fontSize: 11, color: i === 1 ? 'var(--fg-strong)' : 'var(--fg-muted)', fontWeight: i === 1 ? 700 : 400, cursor: 'pointer' }}>{label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SkeletonCell({ width = 56 }: { width?: number }) {
  return <span className="skeleton" style={{ width }} />;
}

function VideoThumb({ src, width = 40, height = 56, borderRadius = 6 }: { src: string; width?: number; height?: number; borderRadius?: number }) {
  return (
    <video
      src={src + '#t=0.001'}
      preload="metadata"
      muted
      playsInline
      style={{ width, height, borderRadius, objectFit: 'cover', display: 'block', flexShrink: 0 }}
    />
  );
}

function filenameFromUrl(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    return decodeURIComponent(url.split('?')[0].split('/').pop() ?? '') || undefined;
  } catch {
    return url.split('?')[0].split('/').pop() || undefined;
  }
}

function ScreensTable({ screens, loading }: { screens: any[]; loading?: boolean }) {
  const sorted = [...screens].sort((a, b) => b.plays - a.plays);
  const showSkeletonRows = loading && screens.length === 0;
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>Screen performance</div>
        {!showSkeletonRows && <span style={{ fontSize: 11, color: 'var(--fg-muted)', background: 'var(--bg-surface-2)', padding: '2px 8px', borderRadius: 999 }}>{screens.length} screens</span>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr style={{ background: 'var(--bg-surface-2)' }}>
              {['ID', 'Screen', 'Plays', 'Impressions'].map((h, i) => (
                <th key={h} style={{ textAlign: i >= 2 ? 'right' : 'left', padding: '10px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', borderBottom: '1px solid var(--border-subtle)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showSkeletonRows
              ? Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '10px 14px' }}><SkeletonCell width={64} /></td>
                  <td style={{ padding: '10px 14px' }}><SkeletonCell width={140} /></td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}><SkeletonCell /></td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}><SkeletonCell /></td>
                </tr>
              ))
              : sorted.map(s => (
                <tr key={s.id} className="tbody-row"
                  onClick={() => {
                    window.history.pushState(null, '', `/screens?screen_id=${encodeURIComponent(s.id)}`);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <td style={{ padding: '10px 14px', fontSize: 11.5, color: 'var(--fg-muted)' }} className="num">{s.id}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12.5, fontWeight: 700, color: 'var(--fg-strong)' }}>{s.venue || s.name}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }} className="num">{loading ? <SkeletonCell /> : fmtFull(s.plays)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--fg-strong)' }} className="num">{loading ? <SkeletonCell /> : fmtFull(s.impressions)}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}


function CampaignPicker({ campaigns, selectedCampaign, onSelect }: {
  campaigns: any[]; selectedCampaign: any; onSelect: (c: any) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>Campaign</span>
      <select
        value={selectedCampaign?.id ?? ''}
        onChange={e => {
          const c = campaigns.find(c => c.id === e.target.value) ?? null;
          onSelect(c);
        }}
        style={{
          padding: '7px 28px 7px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-soft)',
          borderRadius: 8, fontSize: 12.5,
          color: 'var(--fg-strong)', fontFamily: 'inherit',
          fontWeight: selectedCampaign ? 700 : 500,
          cursor: 'pointer', outline: 'none',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center',
        }}
      >
        <option value="">All campaigns (aggregated)</option>
        {campaigns.map(c => (
          <option key={c.id} value={c.id}>
            {c.name} · {c.status}
          </option>
        ))}
      </select>
      {selectedCampaign && (
        <button
          onClick={() => onSelect(null)}
          title="Clear selection"
          style={{ background: 'transparent', border: 'none', color: 'var(--fg-muted)', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <Icon d="M18 6L6 18M6 6l12 12" size={13} /> Clear
        </button>
      )}
    </div>
  );
}

function AgeBreakdownCard({ demographics }: { demographics: any }) {
  const top = [...(demographics?.age ?? [])].sort((a: any, b: any) => b.share - a.share).slice(0, 4);
  const max = top[0]?.share ?? 1;
  return (
    <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 124 }}>
      <div className="eyebrow" style={{ marginBottom: 2 }}>Age Breakdown</div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
        {top.map((b: any) => (
          <div key={b.band} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--fg-muted)', minWidth: 36 }}>{b.band}</span>
            <div style={{ flex: 1, height: 5, background: 'var(--bg-surface-3)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${(b.share / max) * 100}%`, height: '100%', background: 'var(--series-impr)', borderRadius: 999 }} />
            </div>
            <span className="num" style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--fg-strong)', minWidth: 28, textAlign: 'right' }}>{b.share}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GenderSplitCard({ demographics }: { demographics: any }) {
  const splits: any[] = demographics?.gender ?? [];
  const total = splits.reduce((s: number, g: any) => s + g.share, 0) || 1;
  const colors = ['var(--series-plays)', 'var(--series-impr)', 'var(--brand-green-500)'];
  return (
    <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 124 }}>
      <div className="eyebrow" style={{ marginBottom: 2 }}>Gender Split</div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10 }}>
        <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', gap: 1 }}>
          {splits.map((g: any, i: number) => (
            <div key={g.label} style={{ width: `${(g.share / total) * 100}%`, background: colors[i % colors.length] }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {splits.map((g: any, i: number) => (
            <div key={g.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors[i % colors.length], flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{g.label}</span>
              <span className="num" style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-strong)' }}>{g.share}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function buildSeries(raw: HourlyPoint[], granularity: string): SeriesPoint[] {
  const buckets = new Map<string, { plays: number; impressions: number; date: string; label: string }>();

  for (const pt of raw) {
    let key: string;
    let label: string;
    let date: string;

    if (granularity === 'Hourly') {
      const h = String(pt.hour_of_day).padStart(2, '0');
      key = `${pt.day}__${h}`;
      date = pt.day;
      const d = new Date(`${pt.day}T00:00:00`);
      const dayFmt = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      label = `${dayFmt} ${pt.hour_of_day}:00`;
    } else if (granularity === 'Monthly') {
      key = pt.day.slice(0, 7);
      date = key + '-01';
      const d = new Date(`${date}T00:00:00`);
      label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else {
      key = pt.day;
      date = pt.day;
      const d = new Date(`${pt.day}T00:00:00`);
      label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    const existing = buckets.get(key);
    if (existing) {
      existing.plays += pt.total_plays ?? 0;
      existing.impressions += pt.total_impressions ?? 0;
    } else {
      buckets.set(key, { plays: pt.total_plays ?? 0, impressions: pt.total_impressions ?? 0, date, label });
    }
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => ({ date: v.date, label: v.label, plays: v.plays, impressions: v.impressions, measurable: 0, views: 0, revenue: 0 }));
}


function ReportingView({ selectedCampaign, onSelectCampaign, campaigns, totals, granularity, setGranularity, activeMetrics, toggleMetric, mapMetric, setMapMetric, activeTab, setActiveTab, tabs, readOnly, series, seriesLoading, campaignScreens, campaignCreatives, creativeCounts, assetsLoading, startDate, endDate, onDateChange, rawHourly, hourlyAverage, demographics, sensorCounts }: {
  selectedCampaign: any; onSelectCampaign: (c: any) => void; campaigns: any[];
  totals: any; granularity: string; setGranularity: (g: string) => void;
  activeMetrics: string[]; toggleMetric: (k: string) => void;
  mapMetric: string; setMapMetric: (k: string) => void;
  activeTab: string; setActiveTab: (t: string) => void; tabs: { key: string; label: string }[];
  readOnly?: boolean; series?: SeriesPoint[]; seriesLoading?: boolean;
  campaignScreens?: Screen[]; campaignCreatives?: CampaignCreative[]; creativeCounts?: EntityTotals; assetsLoading?: boolean;
  startDate: string; endDate: string; onDateChange: (start: string, end: string) => void;
  rawHourly?: HourlyPoint[];
  hourlyAverage?: HourlyAveragePoint[];
  demographics?: Demographics | null;
  sensorCounts?: { enabled: number; total: number } | null;
}) {
  const [exportOpen, setExportOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  if (selectedCampaign?.status === 'Scheduled') {
    return (
      <div className="card" style={{ padding: 60, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(245,158,11,0.10)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.30)', padding: '6px 14px', borderRadius: 999, marginBottom: 20, fontSize: 12, fontWeight: 700 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} /> Scheduled
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 6 }}>{selectedCampaign.name}</div>
        <div style={{ fontSize: 14, color: 'var(--fg-muted)', marginBottom: 24 }}>
          {selectedCampaign.flight
            ? <>Campaign launches on <strong className="num" style={{ color: 'var(--fg-strong)' }}>{selectedCampaign.flight.split(' – ')[0]}</strong>. Reporting begins on flight start.</>
            : 'Reporting begins on flight start.'}
        </div>
        <div style={{ display: 'inline-flex', gap: 24, marginBottom: 20 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 4 }}>Budget</div>
            <div className="num" style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg-strong)' }}>{selectedCampaign.budget ? `$${selectedCampaign.budget.toLocaleString()}` : '—'}</div>
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 4 }}>Coverage</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg-strong)' }}>{selectedCampaign.area}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {exportOpen && (
        <ExportModal
          campaign={selectedCampaign}
          startDate={startDate} endDate={endDate}
          onClose={() => setExportOpen(false)}
          onOpenReport={() => setReportOpen(true)}
        />
      )}
      {reportOpen && selectedCampaign && (
        <PopReport
          campaign={selectedCampaign}
          startDate={startDate} endDate={endDate}
          totals={{ plays: totals.plays ?? 0, impressions: totals.impressions ?? 0 }}
          hourlyAverage={hourlyAverage ?? []}
          demographics={demographics ?? null}
          screens={campaignScreens ?? []}
          creatives={campaignCreatives ?? []}
          creativeCounts={creativeCounts ?? {}}
          onClose={() => setReportOpen(false)}
        />
      )}
      {!readOnly && <CampaignPicker campaigns={campaigns} selectedCampaign={selectedCampaign} onSelect={onSelectCampaign} />}
      <ControlsBar granularity={granularity} setGranularity={setGranularity} startDate={startDate} endDate={endDate} onDateChange={onDateChange} onExport={() => setExportOpen(true)} />

      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>Delivery over time</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {SERIES.map(s => {
              const on = activeMetrics.includes(s.key);
              return (
                <button key={s.key} onClick={() => toggleMetric(s.key)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '7px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', border: '1.5px solid ' + (on ? s.color : 'var(--border-soft)'),
                  background: on ? s.color + '18' : 'transparent',
                  color: on ? s.color : 'var(--fg-muted)',
                  transition: 'all 0.15s',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: on ? s.color : 'var(--fg-faint)', flexShrink: 0 }} />
                  <span>{s.label}</span>
                  <span className="num" style={{ fontSize: 13, fontWeight: 800, color: on ? s.color : 'var(--fg-muted)', marginLeft: 2 }}>
                    {totals[s.key as keyof typeof totals] != null ? Number(totals[s.key as keyof typeof totals]).toLocaleString('en-US') : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {seriesLoading ? (
          <div style={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                border: '3px solid var(--border-soft)',
                borderTopColor: 'var(--brand-green-500)',
                animation: 'spin 0.7s linear infinite',
              }} />
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Loading delivery data…</span>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <TimeSeriesChart data={series ?? []} seriesConfig={SERIES} activeKeys={activeMetrics} height={420} />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 16, marginBottom: 16 }}>
        <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>Screens — NYC network</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>Hover a pin for screen name</div>
          </div>
          <NetworkMap screens={campaignScreens ?? []} maxWidth={560} />
        </div>
        <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div className="eyebrow" style={{ marginBottom: 4 }}>Audience</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-strong)' }}>Anonymous demographics</div>
            </div>
          </div>
          {sensorCounts && (
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--brand-green-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
              </svg>
              Based on <span className="num" style={{ fontWeight: 700, color: 'var(--fg-strong)' }}>{sensorCounts.enabled}</span> of <span className="num" style={{ fontWeight: 700, color: 'var(--fg-strong)' }}>{sensorCounts.total}</span> screens with sensors enabled
            </div>
          )}
          {demographics ? (
            <>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 8 }}>Gender split</div>
                <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', background: 'var(--bg-surface-3)' }}>
                  {demographics.gender.map(g => (
                    <div key={g.label} title={`${g.label} ${g.share}%`}
                      style={{ width: `${g.share}%`, background: g.label === 'Female' ? 'var(--brand-green-500)' : 'var(--brand-blue)' }} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  {demographics.gender.map(g => (
                    <div key={g.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.label === 'Female' ? 'var(--brand-green-500)' : 'var(--brand-blue)', display: 'inline-block' }} />
                      <span style={{ color: 'var(--fg-muted)' }}>{g.label}</span>
                      <span className="num" style={{ fontWeight: 700, color: 'var(--fg-strong)' }}>{g.share}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 8 }}>Age bands</div>
                {(() => {
                  const maxShare = Math.max(...demographics.age.map(x => x.share));
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {demographics.age.map(a => (
                        <div key={a.band} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 36px', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--fg-muted)' }}>{a.band}</span>
                          <div style={{ height: 5, background: 'var(--bg-surface-3)', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ width: `${(a.share / maxShare) * 100}%`, height: '100%', background: 'var(--series-impr)', borderRadius: 999 }} />
                          </div>
                          <span className="num" style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--fg-strong)', textAlign: 'right' }}>{a.share}%</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '24px 0', textAlign: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--fg-faint)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-muted)' }}>Not enough data points yet</div>
              <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', maxWidth: 200, lineHeight: 1.5 }}>Audience insights will appear once sufficient sensor signals have been collected.</div>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-subtle)', flex: 1 }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{ background: 'transparent', border: 'none', padding: '10px 14px', borderBottom: activeTab === t.key ? '2px solid var(--brand-green-500)' : '2px solid transparent', color: activeTab === t.key ? 'var(--fg-strong)' : 'var(--fg-muted)', fontWeight: activeTab === t.key ? 700 : 600, fontSize: 12.5, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: -1, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'creatives' && (
        assetsLoading || (campaignCreatives && campaignCreatives.length > 0) ? (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>
                Creatives
                {!assetsLoading && <span style={{ fontSize: 11, color: 'var(--fg-muted)', background: 'var(--bg-surface-2)', padding: '2px 8px', borderRadius: 999, marginLeft: 8 }}>{campaignCreatives!.length}</span>}
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-surface-2)' }}>
                  {['', 'Creative', 'Plays', 'Impressions'].map((h, i) => (
                    <th key={i} style={{ textAlign: i >= 2 ? 'right' : 'left', padding: '10px 16px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assetsLoading && (!campaignCreatives || campaignCreatives.length === 0)
                  ? Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 16px', width: 104 }}><div style={{ width: 80, height: 142, borderRadius: 8, background: 'var(--bg-surface-3)' }} /></td>
                      <td style={{ padding: '12px 16px' }}><SkeletonCell width={180} /></td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}><SkeletonCell /></td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}><SkeletonCell /></td>
                    </tr>
                  ))
                  : (campaignCreatives ?? []).map(creative => {
                    const counts = creativeCounts?.[creative.id];
                    const isVideo = creative.type === 'video' || /\.(mp4|mov|webm)$/i.test(creative.creativeUrl ?? '');
                    const filename = filenameFromUrl(creative.creativeUrl);
                    const displayName = creative.name ?? filename ?? creative.id;
                    return (
                      <tr key={creative.id} className="tbody-row"
                        onClick={() => {
                          if (!creative.creativeUrl) return;
                          window.history.pushState(null, '', `/creatives?creative_url=${encodeURIComponent(creative.creativeUrl)}`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        style={{ borderBottom: '1px solid var(--border-subtle)', cursor: creative.creativeUrl ? 'pointer' : 'default', transition: 'background 0.1s' }}
                        onMouseEnter={e => { if (creative.creativeUrl) (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      >
                        <td style={{ padding: '12px 16px', width: 104, verticalAlign: 'middle' }}>
                          {creative.creativeUrl ? (
                            isVideo
                              ? <VideoThumb src={creative.creativeUrl} width={80} height={142} borderRadius={8} />
                              : <img src={creative.creativeUrl} alt="" style={{ width: 80, height: 142, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
                          ) : isVideo ? (
                            <div style={{ width: 80, height: 142, borderRadius: 8, background: 'linear-gradient(160deg,#1e293b,#0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 24 }}>▶</div>
                          ) : (
                            <div style={{ width: 80, height: 142, borderRadius: 8, background: 'var(--bg-surface-3)' }} />
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{
                              fontSize: 10.5, fontWeight: 700,
                              color: isVideo ? 'var(--brand-blue)' : '#FF5C00',
                              background: isVideo ? 'rgba(96,165,250,0.14)' : 'rgba(255,92,0,0.10)',
                              border: `1px solid ${isVideo ? 'rgba(96,165,250,0.25)' : 'rgba(255,92,0,0.30)'}`,
                              padding: '2px 7px', borderRadius: 5, flexShrink: 0,
                            }}>
                              {isVideo ? '▶ Video' : '◻ Image'}
                            </span>
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--fg-strong)', marginBottom: 3, lineHeight: 1.3 }}>{displayName}</div>
                          {filename && displayName !== filename && (
                            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{filename}</div>
                          )}
                          <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', fontFamily: 'monospace' }}>{creative.id}</div>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'middle' }} className="num">
                          {assetsLoading ? <SkeletonCell /> : (
                            <span style={{ fontWeight: 700, fontSize: 13, color: counts?.plays ? 'var(--fg-strong)' : 'var(--fg-faint)' }}>
                              {counts?.plays ? fmtFull(counts.plays) : '—'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'middle' }} className="num">
                          {assetsLoading ? <SkeletonCell /> : (
                            <span style={{ fontWeight: 700, fontSize: 13, color: counts?.impressions ? 'var(--fg-strong)' : 'var(--fg-faint)' }}>
                              {counts?.impressions ? fmtFull(counts.impressions) : '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>No creatives found for this campaign.</div>
        )
      )}
      {activeTab === 'screens' && <ScreensTable screens={campaignScreens ?? []} loading={assetsLoading} />}
    </>
  );
}

function CampaignDetailPage({ campaignUuid, initialCampaign, onCampaignLoad, totals, granularity, setGranularity, activeMetrics, toggleMetric, mapMetric, setMapMetric, activeTab, setActiveTab, tabs, campaigns, isReach }: {
  campaignUuid: string;
  initialCampaign: Campaign | null;
  onCampaignLoad: (c: Campaign) => void;
  totals: any; granularity: string; setGranularity: (g: string) => void;
  isReach?: boolean;
  activeMetrics: string[]; toggleMetric: (k: string) => void;
  mapMetric: string; setMapMetric: (k: string) => void;
  activeTab: string; setActiveTab: (t: string) => void;
  tabs: { key: string; label: string }[];
  campaigns: Campaign[];
}) {
  const [campaign, setCampaign] = useState<Campaign | null>(initialCampaign);
  const [loading, setLoading] = useState(!initialCampaign);
  const [error, setError] = useState<string | null>(null);
  const [rawHourly, setRawHourly] = useState<HourlyPoint[] | null>(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [hourlyAverage, setHourlyAverage] = useState<HourlyAveragePoint[]>([]);
  const [campaignScreens, setCampaignScreens] = useState<Screen[]>([]);
  const [campaignCreatives, setCampaignCreatives] = useState<CampaignCreative[]>([]);
  const [creativeCounts, setCreativeCounts] = useState<EntityTotals>({});
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [demographics, setDemographics] = useState<Demographics | null>(null);
  const [sensorCounts, setSensorCounts] = useState<{ enabled: number; total: number } | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');

  const initDates = (c: Campaign) => {
    const today = new Date().toISOString().slice(0, 10);
    const yearStart = `${new Date().getFullYear()}-01-01`;
    const start = c.startDate || (isReach ? yearStart : today);
    const end = c.status === 'Ended' ? c.endDate : (isReach && !c.endDate ? today : c.endDate || '');
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  };

  useEffect(() => {
    if (initialCampaign) { setCampaign(initialCampaign); initDates(initialCampaign); return; }
    setLoading(true);
    getCampaign(campaignUuid)
      .then(c => { setCampaign(c); onCampaignLoad(c); initDates(c); })
      .catch(err => setError(err?.message ?? 'Failed to load campaign'))
      .finally(() => setLoading(false));
  }, [campaignUuid]);

  useEffect(() => {
    if (!campaign || !campaign.broadsignId || !selectedStartDate || campaign.status === 'Scheduled') return;
    setSeriesLoading(true);
    setRawHourly(null);
    getCampaignHourlySeries(campaign.id, selectedStartDate, selectedEndDate || undefined)
      .then(data => setRawHourly(data))
      .catch(() => setRawHourly([]))
      .finally(() => setSeriesLoading(false));
  }, [campaign?.id, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (!campaign || !campaign.broadsignId || !selectedStartDate || campaign.status === 'Scheduled') return;
    setHourlyAverage([]);
    getCampaignHourlyAverageSeries(campaign.id, selectedStartDate, selectedEndDate || undefined)
      .then(data => setHourlyAverage(data))
      .catch(() => setHourlyAverage([]));
  }, [campaign?.id, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (!campaign || !campaign.broadsignId || !selectedStartDate || campaign.status === 'Scheduled') return;
    setDemographics(null);
    getCampaignDemographics(campaign.id, selectedStartDate, selectedEndDate || undefined)
      .then(data => setDemographics(data))
      .catch(() => setDemographics(null));
  }, [campaign?.id, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (!campaign || !campaign.broadsignId || !selectedStartDate || campaign.status === 'Scheduled') return;
    setAssetsLoading(true);
    const end = selectedEndDate || undefined;
    const emptyImpressions: CreativeImpressionsResult = { totals: {}, urlMap: {} };
    Promise.all([
      getCampaignCreatives(campaign.id, selectedStartDate, end).catch(() => [] as CampaignCreative[]),
      getCampaignDisplayUnitIds(campaign.id, selectedStartDate, end).catch(() => [] as string[]),
      getCampaignImpressionsByCreative(campaign.id, selectedStartDate, end).catch(() => emptyImpressions),
      getCampaignImpressionsByDisplayUnit(campaign.id, selectedStartDate, end).catch(() => ({} as EntityTotals)),
      getScreensSummary(campaign.id, selectedStartDate, end).catch(() => []),
    ]).then(async ([creatives, displayUnitIds, creativesResult, duCounts, screenSummaries]) => {
      const { totals: cTotals, urlMap } = creativesResult;
      // Fill in missing creativeUrl values from the impressions response (also returns creative_url)
      const knownIds = new Set(creatives.map(c => c.id));
      const supplemented: CampaignCreative[] = [
        ...creatives.map(c => ({ ...c, creativeUrl: c.creativeUrl ?? urlMap[c.id] ?? undefined })),
        ...Object.entries(urlMap)
          .filter(([id]) => !knownIds.has(id))
          .map(([id, url]) => ({ id, creativeUrl: url })),
      ];
      setCampaignCreatives(supplemented);
      setCreativeCounts(cTotals);
      if (screenSummaries.length > 0) {
        setSensorCounts({ enabled: screenSummaries.filter(s => s.sensorEnabled).length, total: screenSummaries.length });
        setCampaignScreens(screenSummaries.map(s => ({
          id: s.id,
          name: s.name,
          venue: s.venue,
          neighborhood: '',
          lat: s.lat,
          lon: s.lon,
          plays: s.plays,
          impressions: s.impressions,
          measurable: 0,
          views: 0,
        })));
      } else {
        const screens = await getScreensByBroadsignIds(displayUnitIds);
        setCampaignScreens(screens.map(s => ({ ...s, plays: duCounts[s.id]?.plays ?? 0, impressions: duCounts[s.id]?.impressions ?? 0 })));
      }
    }).catch(() => {}).finally(() => setAssetsLoading(false));
  }, [campaign?.id, selectedStartDate, selectedEndDate]);

  const series = useMemo(
    () => rawHourly ? buildSeries(rawHourly, granularity) : undefined,
    [rawHourly, granularity]
  );

  const seriesTotals = useMemo(() => {
    if (!rawHourly?.length) return totals;
    return rawHourly.reduce(
      (acc, pt) => ({ plays: acc.plays + (pt.total_plays ?? 0), impressions: acc.impressions + (pt.total_impressions ?? 0) }),
      { plays: 0, impressions: 0 }
    );
  }, [rawHourly, totals]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>Loading campaign…</div>;
  if (error || !campaign) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>{error ?? 'Campaign not found'}</div>;

  return (
    <ReportingView
      selectedCampaign={campaign} onSelectCampaign={setCampaign}
      campaigns={campaigns} totals={seriesTotals} readOnly
      granularity={granularity} setGranularity={setGranularity}
      activeMetrics={activeMetrics} toggleMetric={toggleMetric}
      mapMetric={mapMetric} setMapMetric={setMapMetric}
      activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs}
      series={series} seriesLoading={seriesLoading}
      campaignScreens={campaignScreens} campaignCreatives={campaignCreatives} creativeCounts={creativeCounts} assetsLoading={assetsLoading}
      rawHourly={rawHourly ?? []}
      hourlyAverage={hourlyAverage}
      demographics={demographics}
      sensorCounts={sensorCounts}
      startDate={selectedStartDate} endDate={selectedEndDate} onDateChange={(s, e) => { setSelectedStartDate(s); setSelectedEndDate(e); }}
    />
  );
}

export default function Dashboard({ onLogout, user: initialUser }: { onLogout?: () => void; user?: { first_name?: string; last_name?: string; username?: string; email?: string } }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('pop_theme') || 'light');
  const [collapsed, setCollapsed] = useState(false);
  const [page, setPage] = useState(() => parseRoute(window.location.pathname).page);
  const [campaignUuid, setCampaignUuid] = useState<string | null>(() => parseRoute(window.location.pathname).campaignUuid);
  const [user, setUser] = useState(initialUser);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [granularity, setGranularity] = useState('Daily');
  const [activeMetrics, setActiveMetrics] = useState(['plays']);
  const [activeTab, setActiveTab] = useState('creatives');
  const [mapMetric, setMapMetric] = useState('plays');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedClientUuid, setSelectedClientUuid] = useState<string>(() => localStorage.getItem('pop_client_uuid') ?? '');

  useEffect(() => {
    Promise.all([
      getClients(),
      getAllCampaigns(),
      getReachCampaigns().catch(() => null as Campaign[] | null),
    ]).then(([clientData, allCampaigns, reachCampaigns]) => {
      const uuidsWithCampaigns = new Set(
        allCampaigns.map(c => c.client?.uuid).filter(Boolean)
      );
      const filtered = clientData.filter(c => uuidsWithCampaigns.has(c.uuid));
      const displayClients: ClientRecord[] = reachCampaigns !== null
        ? [...filtered, { uuid: '__reach__', identifier: 'reach', name: 'Reach' }]
        : filtered;
      setClients(displayClients);
      setSelectedClientUuid(prev => {
        if (prev && displayClients.some(c => c.uuid === prev)) return prev;
        const first = displayClients[0]?.uuid ?? '';
        if (first) localStorage.setItem('pop_client_uuid', first);
        return first;
      });
    })
    .catch(() => {})
    .finally(() => setClientsLoading(false));
  }, []);

  const handleClientChange = (uuid: string) => {
    setSelectedClientUuid(uuid);
    localStorage.setItem('pop_client_uuid', uuid);
    setSelectedCampaign(null);
    setPage('campaigns');
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('pop_theme', theme);
  }, [theme]);

  useEffect(() => {
    const onPop = () => {
      const { page: p, campaignUuid: u } = parseRoute(window.location.pathname);
      setPage(p);
      setCampaignUuid(u);
      if (!u) setSelectedCampaign(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (path: string) => {
    window.history.pushState(null, '', path);
    const { page: p, campaignUuid: u } = parseRoute(path);
    setPage(p);
    setCampaignUuid(u);
  };

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  const toggleMetric = (key: string) => {
    setActiveMetrics([key]);
  };

  const openCampaign = (c: Campaign) => {
    setSelectedCampaign(c);
    navigate(`/campaigns/${c.id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const tabs = [
    { key: 'creatives', label: 'Creatives' },
    { key: 'screens',   label: 'Screens' },
  ];

  const selectedClient = clients.find(c => c.uuid === selectedClientUuid);

  const topBarProps: any = (() => {
    if (page === 'campaigns') return { eyebrow: selectedClient ? selectedClient.name : 'Smartstreet · POP', title: 'Campaigns' };
    if (page === 'creatives') return { eyebrow: 'Smartstreet · Reporting', title: 'Creatives library' };
    if (page === 'screens') return { eyebrow: 'Smartstreet · Network', title: 'Screens' };
    if (page === 'audience') return { eyebrow: 'Smartstreet · Audience Intelligence', title: 'Audience', subtitle: 'Network-wide audience insights · sensor-verified, anonymous' };
    // campaign detail — title populated by CampaignDetailPage once loaded
    return {
      eyebrow: 'Proof of Performance',
      title: selectedCampaign?.name ?? '…',
      subtitle: selectedCampaign ? (() => {
        const dotColor = selectedCampaign.status === 'Active' ? 'var(--brand-green-500)' : selectedCampaign.status === 'Scheduled' ? '#f59e0b' : 'var(--fg-faint)';
        const clientName = selectedCampaign.client?.name;
        return <span><span style={{ color: dotColor, fontWeight: 700 }}>●</span>{clientName ? ` ${clientName}` : ''}{selectedCampaign.flight ? ` · ${selectedCampaign.flight}` : ''}{selectedCampaign.area ? ` · ${selectedCampaign.area}` : ''}</span>;
      })() : undefined,
      backTo: 'campaigns',
      onBack: () => navigate('/campaigns'),
    };
  })();

  const totals = {
    plays: selectedCampaign?.plays ?? 0,
    impressions: selectedCampaign?.impressions ?? 0,
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-app)' }}>
      <Sidebar page={page} setPage={(p) => navigate('/' + p)} collapsed={collapsed} setCollapsed={setCollapsed} theme={theme} toggleTheme={toggleTheme} onLogout={onLogout} user={user} clients={clients} clientsLoading={clientsLoading} selectedClientUuid={selectedClientUuid} onClientChange={handleClientChange} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar theme={theme} toggleTheme={toggleTheme} user={user} {...topBarProps} />
        <div style={{ padding: 24, flex: 1 }}>
          {page === 'campaigns' && <CampaignsPage onOpenCampaign={openCampaign} clientUuid={selectedClientUuid} onLoad={setCampaigns} />}
          {page === 'creatives' && <CreativesPage campaigns={campaigns} clientUuid={selectedClientUuid} />}
          {page === 'screens'   && <ScreensPage campaigns={campaigns} clientUuid={selectedClientUuid} />}
          {page === 'campaign'  && campaignUuid && (
            <CampaignDetailPage
              campaignUuid={campaignUuid}
              initialCampaign={selectedCampaign?.id === campaignUuid ? selectedCampaign : null}
              onCampaignLoad={setSelectedCampaign}
              isReach={selectedClientUuid === '__reach__'}
              totals={totals} granularity={granularity} setGranularity={setGranularity}
              activeMetrics={activeMetrics} toggleMetric={toggleMetric}
              mapMetric={mapMetric} setMapMetric={setMapMetric}
              activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs}
              campaigns={campaigns}
            />
          )}
          {page === 'settings' && (
            <SettingsPage user={user} onUserUpdate={u => setUser(prev => ({ ...prev, ...u }))} />
          )}
          {!['campaign', 'campaigns', 'creatives', 'screens', 'audience', 'settings'].includes(page) && (
            <div className="card" style={{ padding: 60, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>—</div>
              <div style={{ fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 4, fontSize: 16 }}>{page.charAt(0).toUpperCase() + page.slice(1)}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                This section is coming soon. Try{' '}
                <button onClick={() => navigate('/campaigns')} style={{ background: 'none', border: 'none', color: 'var(--brand-green-500)', fontWeight: 700, cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>Campaigns</button>.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
